import React, { useState, useEffect } from 'react';
import { Edit, Save, X } from 'lucide-react';
import {
    fetchDealerLocationTemplateData,
    createDealerLocationTemplateData,
    updateDealerLocationTemplateData,
    deleteDealerLocationTemplateData
} from '../services/apiService';
import { apiClient } from '../config/api';

const DealerLocationFactFindManager = React.forwardRef(({ locationId, factfindTemplate }, ref) => {
    const [locationData, setLocationData] = useState({ sales: [], service: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allFields, setAllFields] = useState({ sales: [], service: [] });
    const [editingFields, setEditingFields] = useState({}); // Track which fields are being edited
    const [editedFields, setEditedFields] = useState(new Set()); // Track which fields have been edited
    const [validationErrors, setValidationErrors] = useState([]);
    const [showExitModal, setShowExitModal] = useState(false);
    const [availableBrands, setAvailableBrands] = useState([]);

    useEffect(() => {
        if (locationId) {
            loadLocationData();
            loadFactfindTemplate();
            loadBrands();
        }
    }, [locationId]);

    const loadBrands = async () => {
        try {
            const brands = await apiClient.get('/brands');
            setAvailableBrands(brands.map(b => b.name));
        } catch (error) {
            console.error('Error loading brands:', error);
        }
    };

    const loadFactfindTemplate = async () => {
        try {
            const templateData = await apiClient.get('/admin/templates/factfind');
            // Transform to match expected format
            const transformed = {
                sales: (templateData.sales || []).map(field => {
                    // Parse field_options if it's a string (JSON)
                    let parsedOptions = field.options || field.field_options;
                    if (typeof parsedOptions === 'string' && parsedOptions) {
                        try {
                            parsedOptions = JSON.parse(parsedOptions);
                        } catch (e) {
                            parsedOptions = null;
                        }
                    }
                    return {
                        id: field.key || field.field_key,
                        key: field.key || field.field_key,
                        label: field.label || field.field_label,
                        type: field.type || field.field_type,
                        options: parsedOptions,
                        is_required: field.is_required,
                        order: field.order || field.display_order || 0
                    };
                }),
                service: (templateData.service || []).map(field => {
                    // Parse field_options if it's a string (JSON)
                    let parsedOptions = field.options || field.field_options;
                    if (typeof parsedOptions === 'string' && parsedOptions) {
                        try {
                            parsedOptions = JSON.parse(parsedOptions);
                        } catch (e) {
                            parsedOptions = null;
                        }
                    }
                    return {
                        id: field.key || field.field_key,
                        key: field.key || field.field_key,
                        label: field.label || field.field_label,
                        type: field.type || field.field_type,
                        options: parsedOptions,
                        is_required: field.is_required,
                        order: field.order || field.display_order || 0
                    };
                })
            };
            setAllFields(transformed);
        } catch (error) {
            console.error('Error loading factfind template:', error);
            // Fallback to prop if API fails
            if (factfindTemplate) {
                setAllFields(factfindTemplate);
            }
        }
    };

    const loadLocationData = async () => {
        try {
            setLoading(true);
            const data = await fetchDealerLocationTemplateData(locationId, 'factfind');
            setLocationData(data);
        } catch (error) {
            console.error('Error loading location fact find data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditField = (fieldType, fieldKey) => {
        const editKey = `${fieldType}_${fieldKey}`;
        const existingField = locationData[fieldType]?.find(field => field.field_key === fieldKey);

        setEditingFields(prev => ({
            ...prev,
            [editKey]: existingField?.field_value || ''
        }));
    };

    const handleSaveField = async (fieldType, fieldKey) => {
        const editKey = `${fieldType}_${fieldKey}`;
        const newValue = (editingFields[editKey] || '').trim();
        const templateField = allFields[fieldType]?.find(field => field.key === fieldKey);

        // Check if required field is empty
        if (templateField?.is_required && newValue === '') {
            alert(`${templateField.label} is required and cannot be left empty.`);
            return;
        }

        try {
            setSaving(true);

            const existingField = locationData[fieldType]?.find(field => field.field_key === fieldKey);

            if (newValue === '') {
                // Delete field if empty and exists
                if (existingField?.instance_id) {
                    await deleteDealerLocationTemplateData(locationId, existingField.instance_id);
                }
            } else {
                if (existingField?.instance_id) {
                    // Update existing
                    await updateDealerLocationTemplateData(locationId, existingField.instance_id, newValue);
                } else {
                    // Create new
                    await createDealerLocationTemplateData(locationId, fieldKey, newValue);
                }
            }

            // Mark as edited and reload data
            setEditedFields(prev => new Set([...prev, editKey]));
            await loadLocationData();

            // Exit edit mode
            setEditingFields(prev => {
                const next = { ...prev };
                delete next[editKey];
                return next;
            });
        } catch (error) {
            console.error('Error updating field:', error);
            alert('Failed to update field. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = (fieldType, fieldKey) => {
        const editKey = `${fieldType}_${fieldKey}`;
        setEditingFields(prev => {
            const next = { ...prev };
            delete next[editKey];
            return next;
        });
    };

    // Validation function to check only edited required fields
    const validateRequiredFields = () => {
        const errors = [];

        ['sales', 'service'].forEach(type => {
            const templateFields = allFields[type] || [];

            templateFields.forEach(templateField => {
                const editKey = `${type}_${templateField.key}`;
                const existingField = locationData[type]?.find(field => field.field_key === templateField.key);
                const currentValue = existingField?.field_value || '';

                // Only validate if this field has been edited and is required
                if (editedFields.has(editKey) && templateField.is_required) {
                    if (!currentValue || currentValue.trim() === '') {
                        errors.push(`${templateField.label} (${type}) is required`);
                    }
                }
            });
        });

        setValidationErrors(errors);

        // Show modal if there are errors
        if (errors.length > 0) {
            setShowExitModal(true);
            return false;
        }

        return true;
    };

    // Expose validation function to parent component
    React.useImperativeHandle(ref, () => ({
        validateRequiredFields
    }));

    const renderFieldsSection = (type) => {
        const templateFields = allFields[type] || [];
        const existingFields = locationData[type] || [];

        if (templateFields.length === 0) {
            return (
                <div key={type} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold mb-3 capitalize">{type} Fact Find</h4>
                    <p className="text-gray-500 text-sm">No {type} fact find fields configured by admin.</p>
                </div>
            );
        }

        return (
            <div key={type} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-3 capitalize">{type} Fact Find</h4>
                <div className="space-y-3">
                    {templateFields.map(templateField => {
                        // Find existing field data
                        const existingField = existingFields.find(
                            field => field.field_key === templateField.key
                        );

                        const editKey = `${type}_${templateField.key}`;
                        const isEditing = editKey in editingFields;
                        const hasBeenEdited = editedFields.has(editKey);
                        const currentValue = existingField?.field_value || '';

                        return (
                            <div key={templateField.id} className="flex items-center gap-2">
                                <label className="w-32 text-sm font-medium text-gray-700">
                                    {templateField.label}
                                    {templateField.is_required && <span className="text-red-500 ml-1">*</span>}
                                    {hasBeenEdited && <span className="text-green-500 ml-1">✓</span>}:
                                </label>
                                <div className="flex-1 flex items-center gap-2">
                                    {isEditing ? (
                                        // Edit mode - render different input types based on field type
                                        <>
                                            {templateField.type === 'textarea' ? (
                                                <textarea
                                                    value={editingFields[editKey]}
                                                    onChange={(e) => setEditingFields(prev => ({
                                                        ...prev,
                                                        [editKey]: e.target.value
                                                    }))}
                                                    placeholder={`Enter ${templateField.label.toLowerCase()}${templateField.is_required ? ' (required)' : ''}`}
                                                    className="flex-1 p-2 border rounded-md text-sm border-blue-300"
                                                    disabled={saving}
                                                    rows={3}
                                                    autoFocus
                                                />
                                            ) : templateField.type === 'brand' ? (
                                                <select
                                                    value={editingFields[editKey]}
                                                    onChange={(e) => setEditingFields(prev => ({
                                                        ...prev,
                                                        [editKey]: e.target.value
                                                    }))}
                                                    className="flex-1 p-2 border rounded-md text-sm border-blue-300"
                                                    disabled={saving}
                                                    autoFocus
                                                >
                                                    <option value="">Select {templateField.label}</option>
                                                    {availableBrands.map(brand => (
                                                        <option key={brand} value={brand}>{brand}</option>
                                                    ))}
                                                </select>
                                            ) : (templateField.type === 'select' && templateField.options) ? (
                                                <select
                                                    value={editingFields[editKey]}
                                                    onChange={(e) => setEditingFields(prev => ({
                                                        ...prev,
                                                        [editKey]: e.target.value
                                                    }))}
                                                    className="flex-1 p-2 border rounded-md text-sm border-blue-300"
                                                    disabled={saving}
                                                    autoFocus
                                                >
                                                    <option value="">Select {templateField.label}</option>
                                                    {Array.isArray(templateField.options) ? templateField.options.map(option => (
                                                        <option key={option} value={option}>{option}</option>
                                                    )) : Object.entries(templateField.options).map(([key, value]) => (
                                                        <option key={key} value={key}>{value}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={templateField.type === 'email' ? 'email' : 
                                                          templateField.type === 'tel' ? 'tel' : 
                                                          templateField.type === 'url' ? 'url' : 
                                                          templateField.type === 'number' ? 'number' : 
                                                          templateField.type === 'date' ? 'date' : 'text'}
                                                    value={editingFields[editKey]}
                                                    onChange={(e) => setEditingFields(prev => ({
                                                        ...prev,
                                                        [editKey]: e.target.value
                                                    }))}
                                                    placeholder={`Enter ${templateField.label.toLowerCase()}${templateField.is_required ? ' (required)' : ''}`}
                                                    className="flex-1 p-2 border rounded-md text-sm border-blue-300"
                                                    disabled={saving}
                                                    autoFocus
                                                />
                                            )}
                                            <button
                                                onClick={() => handleSaveField(type, templateField.key)}
                                                className="p-2 text-green-600 hover:text-green-800"
                                                title="Save changes"
                                                disabled={saving}
                                            >
                                                <Save size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleCancelEdit(type, templateField.key)}
                                                className="p-2 text-gray-600 hover:text-gray-800"
                                                title="Cancel edit"
                                                disabled={saving}
                                            >
                                                <X size={16} />
                                            </button>
                                        </>
                                    ) : (
                                        // Display mode
                                        <>
                                            <div className={`flex-1 p-2 border rounded-md text-sm bg-gray-50 ${
                                                templateField.is_required && !currentValue && hasBeenEdited
                                                    ? 'border-red-300 text-red-700'
                                                    : 'border-gray-300 text-gray-700'
                                            }`}>
                                                {currentValue || `Enter ${templateField.label.toLowerCase()}${templateField.is_required ? ' (required)' : ''}`}
                                            </div>
                                            <button
                                                onClick={() => handleEditField(type, templateField.key)}
                                                className="p-2 text-gray-500 hover:text-gray-700"
                                                title="Edit field"
                                                disabled={saving}
                                            >
                                                <Edit size={16} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">Loading fact find...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Location Fact Find</h3>
                {saving && (
                    <div className="text-sm text-blue-600">Saving changes...</div>
                )}
            </div>

            <div className="space-y-4">
                {renderFieldsSection('sales')}
                {renderFieldsSection('service')}
            </div>

            <div className="text-xs text-gray-500 mt-4">
                Click the edit icon to modify fields.
                <br />
                <span className="text-red-500">*</span> Required fields must be completed before leaving the page.
                <span className="text-green-500 ml-2">✓</span> Indicates edited fields.
            </div>

            {/* Exit Warning Modal */}
            {showExitModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">Incomplete Required Fields</h3>
                        <p className="text-gray-700 mb-4">
                            You have unfinished required fields that need to be completed:
                        </p>
                        <ul className="list-disc pl-6 mb-6 space-y-1">
                            {validationErrors.map((error, index) => (
                                <li key={index} className="text-sm text-red-600">{error}</li>
                            ))}
                        </ul>
                        <p className="text-gray-700 mb-6">
                            Are you sure you want to leave this page without completing these fields?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowExitModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Stay and Complete
                            </button>
                            <button
                                onClick={() => {
                                    setShowExitModal(false);
                                    setValidationErrors([]);
                                    // Allow navigation by returning true
                                    window.history.back();
                                }}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                                Leave Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default DealerLocationFactFindManager;